import React, { useState } from 'react'

import { Modal } from '@components/common/modal'
import { VendorPricingAgreementTiers } from '@components/common'

import {
  VendorPricingAgreement,
  VendorPricingAgreementServiceType,
  VendorPricingAgreementTier,
} from '@types'

interface EditVendorPricingAgreementModalProps {
  currentUser: { firstName: string; lastName: string; email: string }
  vendorPricingAgreement: VendorPricingAgreement | null

  saveVendorPricingAgreement: (
    data: VendorPricingAgreement,
  ) => Promise<VendorPricingAgreement | null>
  close: () => void
}

const EditVendorPricingAgreementModal: React.FC<
  EditVendorPricingAgreementModalProps
> = ({
  currentUser,
  vendorPricingAgreement,
  saveVendorPricingAgreement,
  close,
}) => {
  const [pricingAgreement, setVendorPricingAgreement] =
    useState<VendorPricingAgreement | null>(
      vendorPricingAgreement
        ? {
            ...vendorPricingAgreement,
            lastUpdatedBy: `${currentUser.firstName} ${currentUser.lastName} (${currentUser.email})`,
          }
        : null,
    )

  if (!pricingAgreement) {
    return null
  }

  const onChangeActive = (value: boolean) => {
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, active: value })
  }

  const onChangeTiers =
    (idx: number, field: keyof VendorPricingAgreementTier) =>
    (value: number) => {
      const tiers = pricingAgreement?.tiers || []
      const newTiers = [...tiers]
      newTiers[idx][field] = value
      pricingAgreement &&
        setVendorPricingAgreement({ ...pricingAgreement, tiers: newTiers })
    }

  const onAddTier = () => {
    const nextTiers = [
      ...(pricingAgreement.tiers || []),
      {
        position: pricingAgreement.tiers.length + 1,
        lowerLimit: 0,
        upperLimit: null,
        discountPercentage: 1,
      },
    ]
    nextTiers.forEach((tier, index) => {
      tier.position = index + 1
    })
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, tiers: nextTiers })
  }

  const onRemoveTier = (index: number) => {
    const nextTiers = [...(pricingAgreement.tiers || [])]
    nextTiers.splice(index, 1)
    nextTiers.forEach((tier, index) => {
      tier.position = index + 1
    })
    pricingAgreement &&
      setVendorPricingAgreement({ ...pricingAgreement, tiers: nextTiers })
  }

  const onChangeServiceTypes = (
    serviceType: VendorPricingAgreementServiceType,
    checked: boolean,
  ) => {
    const nextServiceTypes = [...(pricingAgreement.serviceTypes || [])]
    if (checked) {
      nextServiceTypes.push(serviceType)
    } else {
      nextServiceTypes.splice(nextServiceTypes.indexOf(serviceType), 1)
    }
    pricingAgreement &&
      setVendorPricingAgreement({
        ...pricingAgreement,
        serviceTypes: nextServiceTypes,
      })
  }

  const onSave = async () => {
    const updatedPricingAgreement =
      await saveVendorPricingAgreement(pricingAgreement)
    if (updatedPricingAgreement) {
      close()
    }
  }

  return (
    <Modal
      title={
        pricingAgreement?.id === ''
          ? 'Create Universal Vendor Pricing Agreement'
          : 'Edit Universal Vendor Pricing Agreement'
      }
      noHeader={true}
      hideModal={close}
      color="#001940"
      width="900px"
    >
      <VendorPricingAgreementTiers
        pricingAgreement={pricingAgreement}
        onChangeActive={onChangeActive}
        onChangeServiceTypes={onChangeServiceTypes}
        onChangeTiers={onChangeTiers}
        onAddTier={onAddTier}
        onRemoveTier={onRemoveTier}
        onSave={onSave}
        onCancel={close}
      />
    </Modal>
  )
}

export default EditVendorPricingAgreementModal
